/**
 * API Client for Context Harness Backend
 *
 * Features:
 * - Automatic Bearer token injection (Clerk)
 * - Type-safe API calls
 * - Error handling (401, 403, 500)
 * - Base URL configuration via environment variables
 */

import type {
  // ACL Types
  DefaultACLSettings,
  ACLCategory,
  CreateACLCategoryRequest,
  ShareRequest,
  ShareRequestsResponse,
  ApproveRejectRequest,
  // Adapters Types
  AdapterType,
  AdapterInfo,
  AdaptersListResponse,
  AdapterConnectRequest,
  AdapterConnectResponse,
  AdapterDisconnectResponse,
  // BOX Types
  BoxUploadInput,
  FileUploadMetadata,
  BoxUploadResponse,
  BoxStatusResponse,
  BoxHistoryResponse,
  BoxHistoryItem,
  // Proposals Types
  ProposalListResponse,
  ProposalDetailResponse,
  ProposalActionResponse,
  ProposalStatus,
  // Memory Types
  MemoryCategory,
  MemoryItem,
  MemoryLink,
  MemoryResource,
  MemoryAssetExportResponse,
  SearchRequest,
  SearchResponse,
  SearchResultItem,
  SearchStrategy,
  // Workspace Types
  WorkspaceFeaturesResponse,
  WorkspaceFeaturesData,
  WorkspaceStatusResponse,
  CreateOnboardingWorkspaceRequest,
  OnboardingWorkspaceResponse,
  OnboardingAutoSetupResponse,
  OnboardingPreset,
  VisibleWorkspaceInfo,
  VisibleWorkspaceListResponse,
  VisibleWorkspaceResponse,
  // Workspace Admin Types (Q-1)
  WorkspaceListResponse,
  WorkspaceMember,
  WorkspaceMembersResponse,
  InviteMemberRequest,
  UpdateMemberRoleRequest,
  OverallStatsResponse,
  BenchmarkCurationOverviewResponse,
  BenchmarkCurationPacketListResponse,
  BenchmarkCurationPacketListItem,
  BenchmarkCurationPacketDetailResponse,
  BenchmarkCurationPacketDetail,
  BenchmarkCurationQueueResponse,
  BenchmarkCurationQueueEntry,
  BenchmarkCurationPacketUpdateRequest,
  JLocomoCurationOverviewResponse,
  JLocomoCurationPacketListResponse,
  JLocomoCurationPacketListItem,
  JLocomoCurationPacketDetailResponse,
  JLocomoCurationPacketDetail,
  JLocomoCurationQueueResponse,
  JLocomoCurationQueueEntry,
  JLocomoCurationPacketUpdateRequest,
  JLocomoArtifactsStatusResponse,
  JLocomoReadinessResponse,
  JLocomoCurationValidateRequest,
  JLocomoCurationValidateResponse,
  JLocomoCurationCompileResponse,
  JLocomoCurationSmokeRunRequest,
  JLocomoCurationSmokeRunResponse,
  KpiSummaryResponse,
  KpiStatsResponse,
  MonitoringMetricsResponse,
  MonitoringHealthResponse,
  CatalogSummaryResponse,
  // Access Management Types (UAC-08b)
  AccessPolicy,
  AccessPoliciesListResponse,
  CreateAccessPolicyRequest,
  McpToken,
  McpTokensListResponse,
  CreateMcpTokenRequest,
  CreateMcpTokenResponse,
  // userSlot 選択肢 API Types (UCX-04)
  UserSlotsListResponse,
} from '../types/api';
// WU-07: capability bootstrap のレスポンス契約（capability-config.ts が正本）
import type { MeBootstrapResponse } from './capability-config';
import type {
  MeetingSnapshotHistoryResponse,
  MeetingSnapshotResponse,
} from '../types/meeting-center';
import type {
  GiftAuditEventsResponse,
  IssueMcpCredentialInput,
  IssuedMcpCredential,
  ManagedMcpCredential,
  MeetingImportsResponse,
  MeetingImportApprovalTarget,
  MeetingSourceTestResult,
  MeetingSourcesResponse,
  OnboardGiftOperatorInput,
  OnboardGiftOperatorResult,
  OperatorsResponse,
  UpdateMeetingSourceInput,
} from '../types/gift-operations';
import { buildPresetPolicyEntries } from './access-policy-presets';

// ─── Configuration ───

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// ─── URL Building ───

/**
 * Build full API URL from path
 * Handles both relative (/api) and absolute (https://api.example.com) base URLs
 *
 * @example
 * - API_BASE_URL='/api', path='/acl/settings' → '/api/acl/settings'
 * - API_BASE_URL='https://api.example.com', path='/acl/settings' → 'https://api.example.com/acl/settings'
 */
function buildApiUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL : API_BASE_URL + '/';
  return base + cleanPath;
}

function mutationHeaders(): HeadersInit {
  const idempotencyKey = globalThis.crypto?.randomUUID?.()
    ?? `web-${Date.now()}-${Math.random().toString(36).slice(2)}`
  const correlationId = globalThis.crypto?.randomUUID?.() ?? idempotencyKey
  return { 'Idempotency-Key': idempotencyKey, 'X-Correlation-ID': correlationId }
}

interface RawOperationsPrincipal {
  workspaceId: string
  memberId: string | null
  sessionUserId: string
  displayName?: string | null
  workspaceRole: string | null
  dataUserSlot: string | null
  mcpPrincipalIds: string[]
  accessPolicyPrincipalRef: string | null
  status: 'active' | 'conflict'
  conflicts: string[]
}

interface RawMeetingSource {
  id: string
  provider: 'google_meet' | 'zoom_meeting'
  organizerRefs: string[]
  meetingFilter: {
    externalMeetingIds?: string[]
    externalArtifactIds?: string[]
    conferenceRecord?: string
    meetingId?: string
  }
  freshnessSlaMinutes: number
  enabled: boolean
  credentialState: 'configured' | 'unknown'
  connectionState: 'connected' | 'not_configured' | 'error' | 'unknown' | 'disabled'
  lastSucceededAt: string | null
}

interface RawMeetingImport {
  id: string
  provider: 'google_meet' | 'zoom_meeting'
  status: 'detected' | 'fetching' | 'extracting' | 'review_required' | 'approved' | 'rejected' | 'retryable' | 'failed' | 'deduplicated'
  detectedAt: string
  errorCode: string | null
  reviewedAt: string | null
  candidates: Array<{
    id: string
    kind: 'decision' | 'action' | 'issue' | 'agenda' | 'evidence'
    summary: string
    confidence: number
    reviewStatus: 'pending' | 'approved' | 'rejected'
  }>
}

interface RawOperationsAuditEvent {
  id: string
  actorUserId: string
  resourceId: string
  action: string
  timestamp: string
  metadata: Record<string, unknown>
}

// ─── Freshness Types (WU-06) ───
// core /api/freshness の契約と同期（packages/core/src/api/web-ui/freshness.ts）。
// stale 判定（>24h）はサーバー側固定。クライアントは stale フラグを表示に使うだけ。

export type FreshnessKind = 'catalog' | 'funnel' | 'ingest';

export interface FreshnessSource {
  kind: FreshnessKind;
  /** データの最終更新時刻（ISO8601）。1 件もなければ null */
  asOf: string | null;
  /** 経過時間（時間）。asOf=null なら null */
  staleHours: number | null;
  /** 24h 超過または鮮度不明なら true */
  stale: boolean;
}

export interface FreshnessResponse {
  sources: FreshnessSource[];
  generatedAt: string;
}

// ─── Error Types ───

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Core API Client ───

export interface ApiClientOptions {
  getToken?: () => Promise<string | null>;
}

class ApiClient {
  private getToken?: () => Promise<string | null>;

  constructor(options?: ApiClientOptions) {
    this.getToken = options?.getToken;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Build URL from base + path
    const url = buildApiUrl(path);

    const headers: Record<string, string> = {};

    // Only set Content-Type for JSON body (not FormData/Blob)
    const body = options.body;
    if (body && typeof body === 'string') {
      headers['Content-Type'] = 'application/json';
    }

    // Copy existing headers
    if (options.headers) {
      const existingHeaders = new Headers(options.headers);
      existingHeaders.forEach((value, key) => {
        headers[key] = value;
      });
    }

    // Add Bearer token if available
    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle error responses
    if (!response.ok) {
      // Try JSON first, fallback to text for error details
      let errorData: unknown = null;
      let errorMessage = `Request failed with status ${response.status}`;

      try {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          errorData = await response.json();
          errorMessage = (errorData as { error?: string })?.error || errorMessage;
        } else {
          const text = await response.text();
          if (text) {
            errorMessage = text.slice(0, 200); // Limit error message length
            errorData = { text };
          }
        }
      } catch {
        // Ignore parsing errors, use default message
      }

      switch (response.status) {
        case 401:
          throw new ApiError('Unauthorized - Please log in', 401, errorData);
        case 403:
          throw new ApiError('Forbidden - You do not have permission', 403, errorData);
        case 404:
          throw new ApiError('Not found', 404, errorData);
        default:
          throw new ApiError(errorMessage, response.status, errorData);
      }
    }

    // Handle empty responses (204 No Content, etc.)
    const contentType = response.headers.get('content-type');
    if (response.status === 204 || !contentType?.includes('application/json')) {
      return null as T;
    }

    const text = await response.text();
    return text ? JSON.parse(text) : (null as T);
  }

  // ─── ACL API ───

  async getACLSettings(workspaceId?: string): Promise<DefaultACLSettings> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    return this.request<DefaultACLSettings>(`/acl/settings${qs}`);
  }

  async updateACLSettings(settings: DefaultACLSettings, workspaceId?: string): Promise<DefaultACLSettings> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    return this.request<DefaultACLSettings>(`/acl/settings${qs}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getACLCategories(workspaceId?: string): Promise<ACLCategory[]> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    const response = await this.request<{ categories: ACLCategory[] }>(`/acl/categories${qs}`);
    return response.categories;
  }

  async createACLCategory(category: CreateACLCategoryRequest, workspaceId?: string): Promise<ACLCategory> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    return this.request<ACLCategory>(`/acl/categories${qs}`, {
      method: 'POST',
      body: JSON.stringify(category),
    });
  }

  async getShareRequests(workspaceId?: string): Promise<ShareRequest[]> {
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    const response = await this.request<ShareRequestsResponse>(`/acl/requests${qs}`);
    return response.requests;
  }

  async approveShareRequest(requestId: string, reason?: string, workspaceId?: string): Promise<ShareRequest> {
    const body: ApproveRejectRequest = { reason };
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    const response = await this.request<{ success: boolean; request: ShareRequest }>(
      `/acl/requests/${requestId}/approve${qs}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
    return response.request;
  }

  async rejectShareRequest(requestId: string, reason?: string, workspaceId?: string): Promise<ShareRequest> {
    const body: ApproveRejectRequest = { reason };
    const qs = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    const response = await this.request<{ success: boolean; request: ShareRequest }>(
      `/acl/requests/${requestId}/reject${qs}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
    return response.request;
  }

  // ─── Adapters API ───

  async getAdapters(workspaceId: string): Promise<AdapterInfo[]> {
    const response = await this.request<AdaptersListResponse>(
      `/adapters?workspaceId=${encodeURIComponent(workspaceId)}`
    );
    return response.adapters;
  }

  async getAdapterStatus(type: AdapterType, workspaceId: string): Promise<AdapterInfo> {
    return this.request<AdapterInfo>(
      `/adapters/${type}/status?workspaceId=${encodeURIComponent(workspaceId)}`
    );
  }

  async connectAdapter(
    type: AdapterType,
    workspaceId: string,
    options?: {
      dryRun?: boolean;
      config?: Record<string, unknown>;
      credentialsRef?: string;
      healthCheckUrl?: string;
    }
  ): Promise<AdapterConnectResponse> {
    const body: AdapterConnectRequest = {
      workspaceId,
      dryRun: options?.dryRun ?? false,
    };
    if (options?.config !== undefined) {
      body.config = options.config;
    }
    if (options?.credentialsRef !== undefined) {
      body.credentialsRef = options.credentialsRef;
    }
    if (options?.healthCheckUrl !== undefined) {
      body.healthCheckUrl = options.healthCheckUrl;
    }
    return this.request<AdapterConnectResponse>(`/adapters/${type}/connect`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async disconnectAdapter(
    type: AdapterType,
    workspaceId: string,
    dryRun = false,
  ): Promise<AdapterDisconnectResponse> {
    return this.request<AdapterDisconnectResponse>(`/adapters/${type}/disconnect`, {
      method: 'POST',
      body: JSON.stringify({ workspaceId, dryRun }),
    });
  }

  async startHeartbeat(workspaceId: string): Promise<{ success: boolean; message?: string }> {
    return this.request<{ success: boolean; message?: string }>(
      `/adapters/heartbeat/start?workspaceId=${encodeURIComponent(workspaceId)}`,
      { method: 'POST' },
    );
  }

  // ─── BOX API ───

  async uploadFile(input: BoxUploadInput): Promise<BoxUploadResponse> {
    const formData = new FormData();
    formData.append('file', input.file);
    formData.append('workspaceId', input.metadata.workspaceId);
    formData.append('filename', input.metadata.filename);
    formData.append('size', String(input.metadata.size));
    formData.append('mimeType', input.metadata.mimeType);
    if (input.metadata.tags && input.metadata.tags.length > 0) {
      formData.append('tags', JSON.stringify(input.metadata.tags));
    }
    if (input.metadata.category) {
      formData.append('category', input.metadata.category);
    }
    if (input.metadata.visibility) {
      formData.append('visibility', input.metadata.visibility);
    }

    return this.request<BoxUploadResponse>('/box/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async getUploadStatus(uploadId: string): Promise<BoxStatusResponse> {
    return this.request<BoxStatusResponse>(`/box/status/${uploadId}`);
  }

  async getUploadHistory(
    workspaceId: string,
    page = 1,
    pageSize = 20,
  ): Promise<BoxHistoryResponse> {
    const query = new URLSearchParams({
      workspaceId,
      page: String(page),
      pageSize: String(pageSize),
    });
    return this.request<BoxHistoryResponse>(`/box/history?${query.toString()}`);
  }

  async cancelUpload(uploadId: string): Promise<boolean> {
    const response = await this.request<{ success: boolean }>(`/box/cancel/${uploadId}`, {
      method: 'DELETE',
    });
    return response.success;
  }

  // ─── Proposals API ───

  async getProposals(params?: {
    workspaceId?: string;
    status?: ProposalStatus[];
    templateId?: string;
    limit?: number;
    offset?: number;
  }): Promise<ProposalListResponse> {
    const query = new URLSearchParams();
    if (params?.workspaceId) query.set('workspaceId', params.workspaceId);
    if (params?.status && params.status.length > 0) {
      query.set('status', params.status.join(','));
    }
    if (params?.templateId) query.set('templateId', params.templateId);
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query.set('offset', String(params.offset));

    const suffix = query.toString();
    const path = suffix ? `/proposals?${suffix}` : '/proposals';
    return this.request<ProposalListResponse>(path);
  }

  async getProposalDetail(id: string): Promise<ProposalDetailResponse> {
    return this.request<ProposalDetailResponse>(`/proposals/${id}`);
  }

  async approveProposal(id: string, reason?: string): Promise<ProposalActionResponse> {
    const body: ApproveRejectRequest = { reason };
    return this.request<ProposalActionResponse>(`/proposals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async rejectProposal(id: string, reason?: string): Promise<ProposalActionResponse> {
    const body: ApproveRejectRequest = { reason };
    return this.request<ProposalActionResponse>(`/proposals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ─── Workspaces API ───

  async getWorkspaceFeatures(workspaceId: string): Promise<WorkspaceFeaturesResponse> {
    return this.request<WorkspaceFeaturesResponse>(`/workspaces/${workspaceId}/features`);
  }

  async updateWorkspaceFeatures(
    workspaceId: string,
    features: Partial<WorkspaceFeaturesData>,
  ): Promise<WorkspaceFeaturesResponse> {
    return this.request<WorkspaceFeaturesResponse>(`/workspaces/${workspaceId}/features`, {
      method: 'PUT',
      body: JSON.stringify({ features }),
    });
  }

  async getWorkspaceStatus(workspaceId: string): Promise<WorkspaceStatusResponse> {
    return this.request<WorkspaceStatusResponse>(`/onboarding/workspaces/${workspaceId}/status`);
  }

  async createOnboardingWorkspace(
    input: CreateOnboardingWorkspaceRequest,
  ): Promise<OnboardingWorkspaceResponse> {
    return this.request<OnboardingWorkspaceResponse>('/onboarding/workspaces', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getOnboardingWorkspace(workspaceId: string): Promise<OnboardingWorkspaceResponse> {
    return this.request<OnboardingWorkspaceResponse>(
      `/onboarding/workspaces/${encodeURIComponent(workspaceId)}`
    );
  }

  async startOnboardingAutoSetup(
    workspaceId: string,
    preset: OnboardingPreset,
  ): Promise<OnboardingAutoSetupResponse> {
    return this.request<OnboardingAutoSetupResponse>(
      `/onboarding/workspaces/${encodeURIComponent(workspaceId)}/auto-setup`,
      {
        method: 'POST',
        body: JSON.stringify({ preset }),
      }
    );
  }

  // ─── Memory API ───

  async getMemoryCategories(): Promise<MemoryCategory[]> {
    const response = await this.request<{ categories: MemoryCategory[] }>('/memory/categories');
    return response.categories;
  }

  async createMemoryCategory(input: Pick<MemoryCategory, 'name' | 'description' | 'summary'>): Promise<MemoryCategory> {
    return this.request<MemoryCategory>('/memory/categories', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getMemoryItems(categoryId: string): Promise<MemoryItem[]> {
    const response = await this.request<{ items: MemoryItem[] }>(`/memory/categories/${categoryId}/items`);
    return response.items;
  }

  async createMemoryItem(
    categoryId: string,
    input: Pick<MemoryItem, 'title' | 'description' | 'tags'>
  ): Promise<MemoryItem> {
    return this.request<MemoryItem>(`/memory/categories/${categoryId}/items`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getMemoryResources(itemId: string): Promise<MemoryResource[]> {
    const response = await this.request<{ resources: MemoryResource[] }>(`/memory/items/${itemId}/resources`);
    return response.resources;
  }

  async createMemoryResource(
    itemId: string,
    input: Pick<MemoryResource, 'sourceType' | 'sourceId' | 'label' | 'excerpt' | 'url' | 'metadata'>
  ): Promise<MemoryResource> {
    return this.request<MemoryResource>(`/memory/items/${itemId}/resources`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getMemoryLinks(itemId: string): Promise<MemoryLink[]> {
    const response = await this.request<{ links: MemoryLink[] }>(`/memory/items/${itemId}/links`);
    return response.links;
  }

  async createMemoryLink(
    itemId: string,
    input: Pick<MemoryLink, 'toItemId' | 'relation'>
  ): Promise<MemoryLink> {
    return this.request<MemoryLink>(`/memory/items/${itemId}/links`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async exportMemoryAssets(): Promise<MemoryAssetExportResponse> {
    return this.request<MemoryAssetExportResponse>('/memory/export');
  }

  async search(input: SearchRequest): Promise<SearchResponse> {
    return this.request<SearchResponse>('/search', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  // ─── Workspace Admin API (Q-1) ───

  /**
   * WU-07: capability bootstrap（起動時 1 回）。
   * レスポンス契約は capability-config.ts / core admin.ts と同期。
   */
  async getMeBootstrap(): Promise<MeBootstrapResponse> {
    return this.request<MeBootstrapResponse>('/admin/me/bootstrap');
  }

  async listWorkspaces(): Promise<WorkspaceListResponse> {
    return this.request<WorkspaceListResponse>('/admin/workspaces');
  }

  async listVisibleWorkspaces(): Promise<VisibleWorkspaceListResponse> {
    return this.request<VisibleWorkspaceListResponse>('/admin/me/workspaces');
  }

  async getPrimaryWorkspace(): Promise<VisibleWorkspaceResponse> {
    return this.request<VisibleWorkspaceResponse>('/admin/me/workspaces/primary');
  }

  async getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMembersResponse> {
    return this.request<WorkspaceMembersResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/members`
    );
  }

  async inviteMember(workspaceId: string, req: InviteMemberRequest): Promise<WorkspaceMember> {
    return this.request<WorkspaceMember>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/members`,
      { method: 'POST', body: JSON.stringify(req) }
    );
  }

  async updateMemberRole(
    workspaceId: string,
    memberId: string,
    req: UpdateMemberRoleRequest
  ): Promise<WorkspaceMember> {
    return this.request<WorkspaceMember>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(memberId)}`,
      { method: 'PATCH', body: JSON.stringify(req) }
    );
  }

  async removeMember(workspaceId: string, memberId: string): Promise<void> {
    await this.request<null>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(memberId)}`,
      { method: 'DELETE' }
    );
  }

  async getOverallStats(period: 'day' | 'week' | 'month' = 'week'): Promise<OverallStatsResponse> {
    return this.request<OverallStatsResponse>(`/admin/stats?period=${period}`);
  }

  async getBenchmarkCurationOverview(): Promise<BenchmarkCurationOverviewResponse> {
    return this.request<BenchmarkCurationOverviewResponse>('/admin/benchmark-curation/overview');
  }

  async getBenchmarkCurationQueue(params?: {
    limit?: number;
    includeClosed?: boolean;
  }): Promise<BenchmarkCurationQueueResponse> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.includeClosed !== undefined) query.set('includeClosed', String(params.includeClosed));
    const suffix = query.toString();
    return this.request<BenchmarkCurationQueueResponse>(
      `/admin/benchmark-curation/queue${suffix ? `?${suffix}` : ''}`
    );
  }

  async listBenchmarkCurationPackets(params?: {
    status?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<BenchmarkCurationPacketListResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.q) query.set('q', params.q);
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query.set('offset', String(params.offset));
    const suffix = query.toString();
    return this.request<BenchmarkCurationPacketListResponse>(
      `/admin/benchmark-curation/packets${suffix ? `?${suffix}` : ''}`
    );
  }

  async getBenchmarkCurationPacket(packetId: string): Promise<BenchmarkCurationPacketDetailResponse> {
    return this.request<BenchmarkCurationPacketDetailResponse>(
      `/admin/benchmark-curation/packets/${encodeURIComponent(packetId)}`
    );
  }

  async updateBenchmarkCurationPacket(
    packetId: string,
    input: BenchmarkCurationPacketUpdateRequest,
  ): Promise<BenchmarkCurationPacketDetailResponse> {
    const response = await this.request<{ packet: BenchmarkCurationPacketDetailResponse["packet"] }>(
      `/admin/benchmark-curation/packets/${encodeURIComponent(packetId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      }
    );
    return {
      packet: response.packet,
      queueMeta: null,
    };
  }

  async getJLocomoCurationOverview(): Promise<JLocomoCurationOverviewResponse> {
    return this.request<JLocomoCurationOverviewResponse>('/admin/j-locomo-curation/overview');
  }

  async getJLocomoCurationQueue(params?: {
    limit?: number;
    includeClosed?: boolean;
  }): Promise<JLocomoCurationQueueResponse> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.includeClosed !== undefined) query.set('includeClosed', String(params.includeClosed));
    const suffix = query.toString();
    return this.request<JLocomoCurationQueueResponse>(
      `/admin/j-locomo-curation/queue${suffix ? `?${suffix}` : ''}`
    );
  }

  async listJLocomoCurationPackets(params?: {
    status?: string;
    slice?: string;
    split?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<JLocomoCurationPacketListResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.slice) query.set('slice', params.slice);
    if (params?.split) query.set('split', params.split);
    if (params?.q) query.set('q', params.q);
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query.set('offset', String(params.offset));
    const suffix = query.toString();
    return this.request<JLocomoCurationPacketListResponse>(
      `/admin/j-locomo-curation/packets${suffix ? `?${suffix}` : ''}`
    );
  }

  async getJLocomoCurationPacket(packetId: string): Promise<JLocomoCurationPacketDetailResponse> {
    return this.request<JLocomoCurationPacketDetailResponse>(
      `/admin/j-locomo-curation/packets/${encodeURIComponent(packetId)}`
    );
  }

  async updateJLocomoCurationPacket(
    packetId: string,
    input: JLocomoCurationPacketUpdateRequest,
  ): Promise<JLocomoCurationPacketDetailResponse> {
    return this.request<JLocomoCurationPacketDetailResponse>(
      `/admin/j-locomo-curation/packets/${encodeURIComponent(packetId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      }
    );
  }

  async getJLocomoArtifactsStatus(): Promise<JLocomoArtifactsStatusResponse> {
    return this.request<JLocomoArtifactsStatusResponse>('/admin/j-locomo-curation/artifacts/status')
  }

  async getJLocomoReadiness(): Promise<JLocomoReadinessResponse> {
    return this.request<JLocomoReadinessResponse>('/admin/j-locomo-curation/readiness')
  }

  async validateJLocomoCuration(
    input: JLocomoCurationValidateRequest = {},
  ): Promise<JLocomoCurationValidateResponse> {
    return this.request<JLocomoCurationValidateResponse>('/admin/j-locomo-curation/actions/validate', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  async compileJLocomoCuration(): Promise<JLocomoCurationCompileResponse> {
    return this.request<JLocomoCurationCompileResponse>('/admin/j-locomo-curation/actions/compile', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  async runJLocomoSmokeBenchmark(
    input: JLocomoCurationSmokeRunRequest,
  ): Promise<JLocomoCurationSmokeRunResponse> {
    return this.request<JLocomoCurationSmokeRunResponse>('/admin/j-locomo-curation/actions/smoke-run', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  // ─── KPI API ───

  async getKpiSummary(workspaceId: string): Promise<KpiSummaryResponse> {
    return this.request<KpiSummaryResponse>(`/kpi/summary?workspaceId=${encodeURIComponent(workspaceId)}`)
  }

  async getKpiStats(period: 'week' | 'month', workspaceId: string): Promise<KpiStatsResponse> {
    return this.request<KpiStatsResponse>(`/kpi/stats?period=${period}&workspaceId=${encodeURIComponent(workspaceId)}`)
  }

  // ─── Monitoring API ───

  async getMonitoringMetrics(periodDays = 7): Promise<MonitoringMetricsResponse> {
    return this.request<MonitoringMetricsResponse>(`/monitoring/metrics?periodDays=${periodDays}`)
  }

  async getMonitoringHealth(): Promise<MonitoringHealthResponse> {
    return this.request<MonitoringHealthResponse>('/monitoring/health')
  }

  // ─── Catalog API (掌握マップ, CL-05) ───

  async getCatalogSummary(sfOrgId?: string): Promise<CatalogSummaryResponse> {
    const qs = sfOrgId ? `?sfOrgId=${encodeURIComponent(sfOrgId)}` : ''
    return this.request<CatalogSummaryResponse>(`/catalog/kpi${qs}`)
  }

  // ─── Freshness API (WU-06) ───

  async getFreshness(): Promise<FreshnessResponse> {
    return this.request<FreshnessResponse>('/freshness')
  }

  // ─── Weekly Meeting Center API ───

  async getMeetingCenterLatest(
    workspaceId: string,
  ): Promise<MeetingSnapshotResponse | null> {
    const query = new URLSearchParams({ workspaceId })
    try {
      return await this.request<MeetingSnapshotResponse | null>(
        `/meeting-center/latest?${query.toString()}`,
      )
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }

  async getMeetingCenterHistory(
    workspaceId: string,
  ): Promise<MeetingSnapshotHistoryResponse> {
    const query = new URLSearchParams({ workspaceId })
    return this.request<MeetingSnapshotHistoryResponse>(
      `/meeting-center/history?${query.toString()}`,
    )
  }

  async getMeetingCenterSnapshot(
    workspaceId: string,
    snapshotId: string,
  ): Promise<MeetingSnapshotResponse | null> {
    const query = new URLSearchParams({ workspaceId })
    try {
      return await this.request<MeetingSnapshotResponse | null>(
        `/meeting-center/snapshots/${encodeURIComponent(snapshotId)}?${query.toString()}`,
      )
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }

  // ─── GIFT Operations API ───

  async listGiftOperators(workspaceId: string): Promise<OperatorsResponse> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ operators: RawOperationsPrincipal[] }>(
      `/admin/operators?${query.toString()}`,
    )
    return {
      operators: (response.operators ?? []).map((principal) => ({
        id: principal.memberId ?? principal.sessionUserId,
        workspaceMemberId: principal.memberId,
        sessionUserId: principal.sessionUserId,
        displayName: principal.displayName ?? null,
        dataUserSlot: principal.dataUserSlot,
        mcpPrincipalRef: principal.accessPolicyPrincipalRef,
        role: principal.workspaceRole,
        status: principal.status,
        conflicts: principal.conflicts,
        credentials: principal.mcpPrincipalIds.map((id) => ({
          id,
          name: 'MCP credential',
          keyPrefix: null,
          status: 'active' as const,
          createdAt: null,
          lastUsedAt: null,
        })),
      })),
    }
  }

  async onboardGiftOperator(
    workspaceId: string,
    input: OnboardGiftOperatorInput,
  ): Promise<OnboardGiftOperatorResult> {
    const response = await this.request<
      | { operator: RawOperationsPrincipal; alreadyActive: boolean }
      | { alreadyApplied: true; code: 'duplicate_request' }
    >('/admin/operators/onboard', {
      method: "POST",
      headers: mutationHeaders(),
      body: JSON.stringify({ workspaceId, ...input }),
    })
    if (!('operator' in response)) {
      return {
        operator: null,
        alreadyActive: true,
        alreadyApplied: true,
      }
    }
    const principal = response.operator
    return {
      operator: {
        id: principal.memberId ?? principal.sessionUserId,
        workspaceMemberId: principal.memberId,
        sessionUserId: principal.sessionUserId,
        displayName: principal.displayName ?? null,
        dataUserSlot: principal.dataUserSlot,
        mcpPrincipalRef: principal.accessPolicyPrincipalRef,
        role: principal.workspaceRole,
        status: principal.status,
        conflicts: principal.conflicts,
        credentials: principal.mcpPrincipalIds.map((id) => ({
          id,
          name: "MCP credential",
          keyPrefix: null,
          status: "active" as const,
          createdAt: null,
          lastUsedAt: null,
        })),
      },
      alreadyActive: response.alreadyActive,
      alreadyApplied: false,
    }
  }

  async offboardGiftOperator(workspaceId: string, operatorId: string): Promise<void> {
    await this.request<null>(
      `/admin/operators/${encodeURIComponent(operatorId)}/offboard`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId }) },
    )
  }

  async issueGiftMcpCredential(
    workspaceId: string,
    input: IssueMcpCredentialInput,
  ): Promise<IssuedMcpCredential> {
    const response = await this.request<{
      mcpCredential: ManagedMcpCredential
      rawKey: string
    }>('/admin/mcp-credentials/issue', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({
        workspaceId,
        principalUserId: input.operatorId,
        name: input.name,
        policies: buildPresetPolicyEntries(input.profile),
      }),
    })
    return { credential: response.mcpCredential, rawKey: response.rawKey }
  }

  async reissueGiftMcpCredential(workspaceId: string, credentialId: string): Promise<IssuedMcpCredential> {
    const response = await this.request<{ mcpCredential: ManagedMcpCredential; rawKey: string }>(
      `/admin/mcp-credentials/${encodeURIComponent(credentialId)}/reissue`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId }) },
    )
    return { credential: response.mcpCredential, rawKey: response.rawKey }
  }

  async revokeGiftMcpCredential(workspaceId: string, credentialId: string): Promise<void> {
    await this.request<null>(
      `/admin/mcp-credentials/${encodeURIComponent(credentialId)}/revoke`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId }) },
    )
  }

  async listMeetingSources(workspaceId: string): Promise<MeetingSourcesResponse> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ items: RawMeetingSource[] }>(
      `/admin/meeting-sources?${query.toString()}`,
    )
    return {
      sources: response.items ?? [],
    }
  }

  async updateMeetingSource(
    workspaceId: string,
    sourceId: string,
    input: UpdateMeetingSourceInput,
  ): Promise<void> {
    await this.request<null>(
      `/admin/meeting-sources/${encodeURIComponent(sourceId)}`,
      {
        method: 'PUT',
        headers: mutationHeaders(),
        body: JSON.stringify({ ...input, workspaceId }),
      },
    )
  }

  async testMeetingSource(workspaceId: string, sourceId: string): Promise<MeetingSourceTestResult> {
    return this.request<MeetingSourceTestResult>(
      `/admin/meeting-sources/${encodeURIComponent(sourceId)}/test`,
      { method: 'POST', body: JSON.stringify({ workspaceId }) },
    )
  }

  async syncMeetingImports(
    workspaceId: string,
    input: { sourceConfigId: string; provider: 'google_meet' | 'zoom_meeting'; externalMeetingId: string; externalArtifactId: string },
  ): Promise<void> {
    await this.request<null>('/admin/meeting-imports/sync', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({ workspaceId, ...input }),
    })
  }

  async listMeetingImports(workspaceId: string): Promise<MeetingImportsResponse> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ items: RawMeetingImport[] }>(
      `/admin/meeting-imports?${query.toString()}`,
    )
    return {
      imports: (response.items ?? []).map((run) => ({
        id: run.id,
        provider: run.provider,
        state: run.status,
        detectedAt: run.detectedAt,
        completedAt: run.reviewedAt,
        reasonCode: run.errorCode,
        candidates: (run.candidates ?? []).map((candidate) => ({
          id: candidate.id,
          kind: candidate.kind,
          summary: candidate.summary,
          confidence: candidate.confidence,
          reviewStatus: candidate.reviewStatus === 'pending'
            ? 'review_required'
            : candidate.reviewStatus,
        })),
      })),
    }
  }

  async getMeetingImportApprovalTarget(
    workspaceId: string,
  ): Promise<MeetingImportApprovalTarget | null> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ target: MeetingImportApprovalTarget | null }>(
      `/admin/meeting-imports/approval-target?${query.toString()}`,
    )
    return response.target
  }

  async approveMeetingImport(
    workspaceId: string,
    importId: string,
    expected: {
      expectedWeekOf: string
      expectedSnapshotId: string
      expectedRevision: number
      targetWorkstreamId: string
    },
  ): Promise<void> {
    await this.request<null>(
      `/admin/meeting-imports/${encodeURIComponent(importId)}/approve`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId, ...expected }) },
    )
  }

  async rejectMeetingImport(workspaceId: string, importId: string): Promise<void> {
    await this.request<null>(
      `/admin/meeting-imports/${encodeURIComponent(importId)}/reject`,
      { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ workspaceId }) },
    )
  }

  async listGiftAuditEvents(workspaceId: string): Promise<GiftAuditEventsResponse> {
    const query = new URLSearchParams({ workspaceId })
    const response = await this.request<{ events: RawOperationsAuditEvent[] }>(
      `/admin/audit-events?${query.toString()}`,
    )
    return {
      events: (response.events ?? []).map((event) => ({
        id: event.id,
        action: event.action,
        actorLabel: event.actorUserId,
        targetLabel: event.resourceId,
        occurredAt: event.timestamp,
        summary: typeof event.metadata.operation === 'string' ? event.metadata.operation : event.action,
      })),
    }
  }

  // ─── Access Management API (UAC-08b) ───

  async listAccessPolicies(workspaceId: string): Promise<AccessPoliciesListResponse> {
    return this.request<AccessPoliciesListResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/access-policies`
    )
  }

  async createAccessPolicy(
    workspaceId: string,
    input: CreateAccessPolicyRequest,
  ): Promise<AccessPolicy> {
    const response = await this.request<{ policy: AccessPolicy }>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/access-policies`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    )
    return response.policy
  }

  async deleteAccessPolicy(workspaceId: string, policyId: string): Promise<void> {
    await this.request<null>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/access-policies/${encodeURIComponent(policyId)}`,
      { method: 'DELETE' }
    )
  }

  async listMcpTokens(workspaceId: string): Promise<McpTokensListResponse> {
    return this.request<McpTokensListResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/mcp-tokens`
    )
  }

  async createMcpToken(
    workspaceId: string,
    input: CreateMcpTokenRequest,
  ): Promise<CreateMcpTokenResponse> {
    return this.request<CreateMcpTokenResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/mcp-tokens`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    )
  }

  async deleteMcpToken(workspaceId: string, tokenId: string): Promise<void> {
    await this.request<null>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/mcp-tokens/${encodeURIComponent(tokenId)}`,
      { method: 'DELETE' }
    )
  }

  /** UCX-05: 同じ userSlot/policy を維持したまま新鍵を発行し、旧鍵を失効する。 */
  async reissueMcpToken(workspaceId: string, tokenId: string): Promise<CreateMcpTokenResponse> {
    return this.request<CreateMcpTokenResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/mcp-tokens/${encodeURIComponent(tokenId)}/reissue`,
      { method: 'POST' }
    )
  }

  // ─── userSlot 選択肢 API (UCX-04) ───

  async listUserSlots(workspaceId: string): Promise<UserSlotsListResponse> {
    return this.request<UserSlotsListResponse>(
      `/admin/workspaces/${encodeURIComponent(workspaceId)}/user-slots`
    )
  }
}

// ─── Singleton Instance ───

let apiClientInstance: ApiClient | null = null;

export function createApiClient(options?: ApiClientOptions): ApiClient {
  apiClientInstance = new ApiClient(options);
  return apiClientInstance;
}

export function getApiClient(): ApiClient {
  if (!apiClientInstance) {
    throw new Error('API client not initialized. Call createApiClient() first.');
  }
  return apiClientInstance;
}

// ─── Export Types ───

export type {
  BoxUploadInput,
  DefaultACLSettings,
  ACLCategory,
  CreateACLCategoryRequest,
  ShareRequest,
  AdapterType,
  AdapterInfo,
  AdapterConnectResponse,
  FileUploadMetadata,
  BoxUploadResponse,
  BoxStatusResponse,
  BoxHistoryItem,
  ProposalListResponse,
  ProposalDetailResponse,
  ProposalActionResponse,
  ProposalStatus,
  MemoryCategory,
  MemoryItem,
  MemoryLink,
  MemoryResource,
  MemoryAssetExportResponse,
  SearchRequest,
  SearchResponse,
  SearchResultItem,
  SearchStrategy,
  WorkspaceFeaturesData,
  WorkspaceFeaturesResponse,
  WorkspaceStatusResponse,
  CreateOnboardingWorkspaceRequest,
  OnboardingWorkspaceResponse,
  OnboardingAutoSetupResponse,
  OnboardingPreset,
  VisibleWorkspaceInfo,
  VisibleWorkspaceListResponse,
  VisibleWorkspaceResponse,
  BenchmarkCurationOverviewResponse,
  BenchmarkCurationPacketListResponse,
  BenchmarkCurationPacketListItem,
  BenchmarkCurationPacketDetailResponse,
  BenchmarkCurationPacketDetail,
  BenchmarkCurationQueueResponse,
  BenchmarkCurationQueueEntry,
  BenchmarkCurationPacketUpdateRequest,
  JLocomoCurationOverviewResponse,
  JLocomoCurationPacketListResponse,
  JLocomoCurationPacketListItem,
  JLocomoCurationPacketDetailResponse,
  JLocomoCurationPacketDetail,
  JLocomoCurationQueueResponse,
  JLocomoCurationQueueEntry,
  JLocomoCurationPacketUpdateRequest,
  JLocomoArtifactsStatusResponse,
  JLocomoCurationValidateRequest,
  JLocomoCurationValidateResponse,
  JLocomoCurationCompileResponse,
  JLocomoCurationSmokeRunRequest,
  JLocomoCurationSmokeRunResponse,
  AccessPolicy,
  AccessPoliciesListResponse,
  CreateAccessPolicyRequest,
  McpToken,
  McpTokensListResponse,
  CreateMcpTokenRequest,
  CreateMcpTokenResponse,
  UserSlotsListResponse,
  MeBootstrapResponse,
};
